import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getLeagues, getTimezone, isFetching } from '../../../selectors/common';
import {
  // eslint-disable-next-line max-len
  FILTER_FIELD_CONFIGS, FILTER_LEGENDS, HISTORICAL_EVENT_FIELD_NAME,
} from '../constants';
import {
  buildQueryParams,
} from '../../../utils/common';
import HistoricalEventsTable from '../HistoricalEventsTable';
import {
  getSoccerHistoricalEvents,
  getSoccerHistoricalLastDate,
  getSoccerHistoricalLastEventId,
} from '../../../selectors/historicalEvents';
import { FETCH_OPTIONS } from '../../../constants';
import HistoricalEventsFilter from '../filter';
import { constructFilterFromQueryParams, formatFilterValueToQueryParamsFormat } from './helpers';
import { aClearHistoricalEventsState } from '../../../reducers/historicalEvents';
import { aGetHistoricalEventsAction } from '../../../actions/historicalEventsActions';

const mapToProps = (state) => ({
  leagues: getLeagues(state),
  timezone: getTimezone(state),
  events: getSoccerHistoricalEvents(state),
  lastEventId: getSoccerHistoricalLastEventId(state),
  lastDate: getSoccerHistoricalLastDate(state),
  isFetching: isFetching(state),
});

const actionsToProps = (dispatch) => ({
  getHistoricalEventsAction: (payload) => dispatch(aGetHistoricalEventsAction(payload)),
  clearHistoricalEventsAction: () => dispatch(aClearHistoricalEventsState()),
});

const HistoricalEvents = (props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const tableScrollableEl = useRef();
  const previousScrollTop = useRef();
  const timezoneRef = useRef();

  const [filterFieldConfigs] = useState(FILTER_FIELD_CONFIGS({ leagues: props.leagues }));
  const [hideEndedMatchesFilter] = useState({ key: 'filterHideEndedMatches', value: 0 });
  const [filterValue, setFilterValue] = useState(filterFieldConfigs.reduce((acc, curr) => {
    // we need to create copies of the object to avoid mutating filterFieldConfigs
    if (Array.isArray(curr.initialValue)) {
      acc[curr.fieldName] = [...curr.initialValue];
    } else if (typeof curr.initialValue === 'object') {
      acc[curr.fieldName] = { ...curr.initialValue };
    } else {
      acc[curr.fieldName] = curr.initialValue;
    }
    return acc;
  }, {
    [hideEndedMatchesFilter.key]: hideEndedMatchesFilter.value,
  }));

  const loadMore = () => {
    // eslint-disable-next-line max-len
    const formattedFilterValue = formatFilterValueToQueryParamsFormat(filterValue, props.timezone);
    formattedFilterValue.push(['lastEventId', props.lastEventId]);
    formattedFilterValue.push(['lastDate', props.lastDate]);
    formattedFilterValue.push([hideEndedMatchesFilter.key, hideEndedMatchesFilter.value]);
    const queryParams = buildQueryParams(formattedFilterValue);

    props.getHistoricalEventsAction({
      queryParams,
      loadMore: true,
    });
  };

  const handleScroll = () => {
    if (props.isFetching[FETCH_OPTIONS.GET_HISTORICAL_EVENTS]) {
      previousScrollTop.current = tableScrollableEl.current.scrollTop;
      return;
    }

    if (!props.lastDate || !props.lastEventId) {
      previousScrollTop.current = tableScrollableEl.current.scrollTop;
      return;
    }

    const tableEl = tableScrollableEl.current;

    if (tableEl.scrollHeight <= Math.ceil(tableEl.offsetHeight + tableEl.scrollTop)
      && previousScrollTop.current !== tableEl.scrollTop) {
      loadMore();
    }
    previousScrollTop.current = tableScrollableEl.current.scrollTop;
  };

  useEffect(() => {
    if (tableScrollableEl.current) {
      previousScrollTop.current = tableScrollableEl.current.scrollTop;
      tableScrollableEl.current.addEventListener('scroll', handleScroll);
    }

    if (location.search) {
      // eslint-disable-next-line max-len
      const filter = constructFilterFromQueryParams(location.search, filterValue);

      props.getHistoricalEventsAction({
        queryParams: `${location.search}&${hideEndedMatchesFilter.key}=${hideEndedMatchesFilter.value}`,
      });
      setFilterValue({ ...filter });
      return;
    }

    props.getHistoricalEventsAction({
      queryParams: `?${hideEndedMatchesFilter.key}=${hideEndedMatchesFilter.value}`,
    });
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (timezoneRef.current !== '' && timezoneRef.current !== props.timezone) {
      const dateFilter = filterFieldConfigs.find(
        // eslint-disable-next-line comma-dangle
        (config) => config.fieldName === HISTORICAL_EVENT_FIELD_NAME.DATE_FILTER
      );
      setFilterValue({
        ...filterValue,
        dateFilter: { ...filterValue.dateFilter, ...dateFilter.initialValue },
      });
    }
    timezoneRef.current = props.timezone;
  // eslint-disable-next-line
  }, [props.timezone]);

  // eslint-disable-next-line
  useEffect(() => {
    const element = tableScrollableEl.current;
    return () => {
      element.removeEventListener('scroll', handleScroll);
      props.clearHistoricalEventsAction();
    };
  // eslint-disable-next-line
  },[]);

  const search = () => {
    // eslint-disable-next-line max-len
    const formattedFilterValue = formatFilterValueToQueryParamsFormat(filterValue, props.timezone);
    formattedFilterValue.push([hideEndedMatchesFilter.key, hideEndedMatchesFilter.value]);
    const queryParams = buildQueryParams(formattedFilterValue);
    tableScrollableEl.current.scrollTop = 0;
    navigate({
      pathname: location.pathname,
      search: queryParams,
    });
    props.getHistoricalEventsAction({
      queryParams,
    });
  };

  const clear = () => {
    const initialFieldValues = filterFieldConfigs.reduce((acc, curr) => {
      acc[curr.fieldName] = curr.initialValue;
      return acc;
    }, { [hideEndedMatchesFilter.key]: hideEndedMatchesFilter.value });
    setFilterValue(initialFieldValues);
    props.getHistoricalEventsAction({ queryParams: `?${hideEndedMatchesFilter.key}=${hideEndedMatchesFilter.value}` });
    navigate({
      pathname: location.pathname,
    });
  };

  const handleChangeFilter = (fieldName, value) => {
    setFilterValue({
      ...filterValue,
      [fieldName]: value,
    });
  };

  return (
    <>
      <HistoricalEventsFilter
        legends={FILTER_LEGENDS}
        fields={filterFieldConfigs}
        onSearch={search}
        onClear={clear}
        filterValue={filterValue}
        onChange={handleChangeFilter}
      />
      <div className="table-wrapper m-t-30">
        <div ref={tableScrollableEl} className="table-responsive fix-15">
          <HistoricalEventsTable
            events={props.events}
            leagues={props.leagues}
            timezone={props.timezone}
          />
        </div>
      </div>
    </>
  );
};

export default connect(mapToProps, actionsToProps)(HistoricalEvents);
